import { compileShader, linkProgram } from './gl-program.helpers';

/**
 * GL resources for the two-pass building silhouette: the building's mass is
 * drawn into an offscreen mask, then a fullscreen pass outlines the mask's
 * boundary (DEV-2576).
 */
export type SilhouetteGlResources = {
  maskProgram: WebGLProgram;
  maskPosition: number;
  maskMatrix: WebGLUniformLocation | null;
  outlineProgram: WebGLProgram;
  outlinePosition: number;
  outlineMask: WebGLUniformLocation | null;
  outlineTexel: WebGLUniformLocation | null;
  outlineColor: WebGLUniformLocation | null;
  outlineWidth: WebGLUniformLocation | null;
  /** One buffer per silhouette, so hover and selection keep separate masks. */
  selectedMassBuffer: WebGLBuffer;
  hoveredMassBuffer: WebGLBuffer;
  quadBuffer: WebGLBuffer;
  framebuffer: WebGLFramebuffer;
  texture: WebGLTexture;
  /** Size the mask texture is currently allocated for. */
  width: number;
  height: number;
};

/** Taps around the ring; enough that a round outline has no visible facets. */
const EDGE_SAMPLE_COUNT = 12;

const MASK_VERTEX_SHADER = `
  precision highp float;
  attribute vec3 a_pos;
  uniform mat4 u_matrix;
  void main() {
    gl_Position = u_matrix * vec4(a_pos, 1.0);
  }
`;

const MASK_FRAGMENT_SHADER = `
  precision mediump float;
  void main() {
    gl_FragColor = vec4(1.0);
  }
`;

const OUTLINE_VERTEX_SHADER = `
  precision highp float;
  attribute vec2 a_pos;
  varying vec2 v_uv;
  void main() {
    v_uv = a_pos * 0.5 + 0.5;
    gl_Position = vec4(a_pos, 0.0, 1.0);
  }
`;

/**
 * Draws the mask dilated by the line width, minus the mask itself: the line
 * lands just outside the building instead of straddling its edge, so it never
 * covers the geometry it outlines. That boundary is exactly the silhouette, no
 * matter which faces or storeys produced it.
 */
const OUTLINE_FRAGMENT_SHADER = `
  precision mediump float;
  uniform sampler2D u_mask;
  uniform vec2 u_texel;
  uniform vec4 u_color;
  uniform float u_width;
  varying vec2 v_uv;

  void main() {
    // Inside the building: this is the mask being subtracted again.
    if (texture2D(u_mask, v_uv).a > 0.5) discard;

    float nearest = 0.0;
    for (int index = 0; index < ${EDGE_SAMPLE_COUNT}; index++) {
      float angle = float(index) * (6.2831853 / float(${EDGE_SAMPLE_COUNT}));
      vec2 offset = vec2(cos(angle), sin(angle)) * u_width * u_texel;
      nearest = max(nearest, texture2D(u_mask, v_uv + offset).a);
    }
    // No mass within the line width: outside the outline altogether.
    if (nearest < 0.5) discard;
    gl_FragColor = u_color;
  }
`;

const createTexture = (
  gl: WebGLRenderingContext,
  width: number,
  height: number,
): WebGLTexture => {
  const texture = gl.createTexture();
  if (!texture) throw new Error('Failed to create silhouette mask texture');
  gl.bindTexture(gl.TEXTURE_2D, texture);
  gl.texImage2D(
    gl.TEXTURE_2D,
    0,
    gl.RGBA,
    width,
    height,
    0,
    gl.RGBA,
    gl.UNSIGNED_BYTE,
    null,
  );
  // Clamp so ring samples outside the canvas read the edge instead of wrapping.
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
  gl.bindTexture(gl.TEXTURE_2D, null);
  return texture;
};

/**
 * Initialize the silhouette programs, buffers and offscreen mask target.
 * @param gl WebGL context provided by the Mapbox custom layer
 */
export const initSilhouetteGl = (
  gl: WebGLRenderingContext,
): SilhouetteGlResources => {
  const maskProgram = linkProgram(
    gl,
    compileShader(gl, gl.VERTEX_SHADER, MASK_VERTEX_SHADER),
    compileShader(gl, gl.FRAGMENT_SHADER, MASK_FRAGMENT_SHADER),
  );
  const outlineProgram = linkProgram(
    gl,
    compileShader(gl, gl.VERTEX_SHADER, OUTLINE_VERTEX_SHADER),
    compileShader(gl, gl.FRAGMENT_SHADER, OUTLINE_FRAGMENT_SHADER),
  );

  const selectedMassBuffer = gl.createBuffer();
  const hoveredMassBuffer = gl.createBuffer();
  const quadBuffer = gl.createBuffer();
  const framebuffer = gl.createFramebuffer();
  if (
    !selectedMassBuffer ||
    !hoveredMassBuffer ||
    !quadBuffer ||
    !framebuffer
  ) {
    throw new Error('Failed to create silhouette WebGL resources');
  }

  gl.bindBuffer(gl.ARRAY_BUFFER, quadBuffer);
  gl.bufferData(
    gl.ARRAY_BUFFER,
    new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]),
    gl.STATIC_DRAW,
  );
  gl.bindBuffer(gl.ARRAY_BUFFER, null);

  const width = Math.max(gl.drawingBufferWidth, 1);
  const height = Math.max(gl.drawingBufferHeight, 1);

  return {
    maskProgram,
    maskPosition: gl.getAttribLocation(maskProgram, 'a_pos'),
    maskMatrix: gl.getUniformLocation(maskProgram, 'u_matrix'),
    outlineProgram,
    outlinePosition: gl.getAttribLocation(outlineProgram, 'a_pos'),
    outlineMask: gl.getUniformLocation(outlineProgram, 'u_mask'),
    outlineTexel: gl.getUniformLocation(outlineProgram, 'u_texel'),
    outlineColor: gl.getUniformLocation(outlineProgram, 'u_color'),
    outlineWidth: gl.getUniformLocation(outlineProgram, 'u_width'),
    selectedMassBuffer,
    hoveredMassBuffer,
    quadBuffer,
    framebuffer,
    texture: createTexture(gl, width, height),
    width,
    height,
  };
};

/**
 * Reallocate the mask when the canvas changed size.
 * @returns The resources, with an up-to-date texture
 */
export const resizeSilhouetteTarget = (
  gl: WebGLRenderingContext,
  resources: SilhouetteGlResources,
): SilhouetteGlResources => {
  const width = Math.max(gl.drawingBufferWidth, 1);
  const height = Math.max(gl.drawingBufferHeight, 1);
  if (width === resources.width && height === resources.height) {
    return resources;
  }
  gl.deleteTexture(resources.texture);
  return {
    ...resources,
    texture: createTexture(gl, width, height),
    width,
    height,
  };
};

/** Free every GL object the silhouette owns. */
export const disposeSilhouetteGl = (
  gl: WebGLRenderingContext,
  resources: SilhouetteGlResources,
): void => {
  gl.deleteProgram(resources.maskProgram);
  gl.deleteProgram(resources.outlineProgram);
  gl.deleteBuffer(resources.selectedMassBuffer);
  gl.deleteBuffer(resources.hoveredMassBuffer);
  gl.deleteBuffer(resources.quadBuffer);
  gl.deleteFramebuffer(resources.framebuffer);
  gl.deleteTexture(resources.texture);
};

/**
 * Render the silhouette outline of the uploaded building mass.
 *
 * The mask is filled once from the building's volume, then each outline is
 * drawn from it. Passing several outlines draws them widest first, which is
 * how a halo is made: a wide light line first, a narrow one over it.
 *
 * @param matrix Projection matrix, already floating-origin corrected
 * @param massBuffer Buffer holding the building's mass triangles
 * @param vertexCount Triangle vertices in {@link massBuffer}
 * @param outlines Colour (RGBA in 0-1) and thickness in device pixels
 */
export const drawSilhouette = ({
  gl,
  resources,
  matrix,
  massBuffer,
  vertexCount,
  outlines,
}: {
  gl: WebGLRenderingContext;
  resources: SilhouetteGlResources;
  matrix: Float32Array;
  massBuffer: WebGLBuffer;
  vertexCount: number;
  outlines: { color: [number, number, number, number]; widthPx: number }[];
}): void => {
  if (vertexCount <= 0 || !outlines.length) return;

  // Restore what Mapbox needs back: it caches GL state in its own wrapper and
  // only re-issues a call when its cached value changes, so anything left
  // changed behind its back stays in effect for its later passes.
  const previous = {
    framebuffer: gl.getParameter(
      gl.FRAMEBUFFER_BINDING,
    ) as WebGLFramebuffer | null,
    viewport: gl.getParameter(gl.VIEWPORT) as Int32Array,
    depthTest: gl.isEnabled(gl.DEPTH_TEST),
    blend: gl.isEnabled(gl.BLEND),
    cullFace: gl.isEnabled(gl.CULL_FACE),
  };

  // Pass one: the mass, flattened into a mask. Depth and culling are off so
  // every face contributes, including walls facing away from the camera.
  gl.bindFramebuffer(gl.FRAMEBUFFER, resources.framebuffer);
  gl.framebufferTexture2D(
    gl.FRAMEBUFFER,
    gl.COLOR_ATTACHMENT0,
    gl.TEXTURE_2D,
    resources.texture,
    0,
  );
  gl.viewport(0, 0, resources.width, resources.height);
  gl.disable(gl.DEPTH_TEST);
  gl.disable(gl.CULL_FACE);
  gl.disable(gl.BLEND);
  gl.clearColor(0, 0, 0, 0);
  gl.clear(gl.COLOR_BUFFER_BIT);

  gl.useProgram(resources.maskProgram);
  gl.uniformMatrix4fv(resources.maskMatrix, false, matrix);
  gl.bindBuffer(gl.ARRAY_BUFFER, massBuffer);
  gl.enableVertexAttribArray(resources.maskPosition);
  gl.vertexAttribPointer(resources.maskPosition, 3, gl.FLOAT, false, 0, 0);
  gl.drawArrays(gl.TRIANGLES, 0, vertexCount);
  gl.disableVertexAttribArray(resources.maskPosition);

  // Pass two: outline the mask's boundary onto the map.
  gl.bindFramebuffer(gl.FRAMEBUFFER, previous.framebuffer);
  gl.viewport(
    previous.viewport[0] ?? 0,
    previous.viewport[1] ?? 0,
    previous.viewport[2] ?? resources.width,
    previous.viewport[3] ?? resources.height,
  );
  gl.enable(gl.BLEND);
  gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);

  gl.useProgram(resources.outlineProgram);
  gl.activeTexture(gl.TEXTURE0);
  gl.bindTexture(gl.TEXTURE_2D, resources.texture);
  gl.uniform1i(resources.outlineMask, 0);
  gl.uniform2f(
    resources.outlineTexel,
    1 / resources.width,
    1 / resources.height,
  );
  gl.bindBuffer(gl.ARRAY_BUFFER, resources.quadBuffer);
  gl.enableVertexAttribArray(resources.outlinePosition);
  gl.vertexAttribPointer(resources.outlinePosition, 2, gl.FLOAT, false, 0, 0);

  for (const outline of [...outlines].sort((a, b) => b.widthPx - a.widthPx)) {
    gl.uniform4f(resources.outlineColor, ...outline.color);
    gl.uniform1f(resources.outlineWidth, outline.widthPx);
    gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
  }
  gl.disableVertexAttribArray(resources.outlinePosition);

  gl.bindTexture(gl.TEXTURE_2D, null);
  gl.bindBuffer(gl.ARRAY_BUFFER, null);
  if (previous.depthTest) gl.enable(gl.DEPTH_TEST);
  else gl.disable(gl.DEPTH_TEST);
  if (previous.cullFace) gl.enable(gl.CULL_FACE);
  else gl.disable(gl.CULL_FACE);
  if (previous.blend) gl.enable(gl.BLEND);
  else gl.disable(gl.BLEND);
};
